// pdf.js loader shared by the inline preview and the full viewer. pdf.js is large (~400 KB): it is loaded on the
// first preview, not with the app. Documents are cached per signed URL so opening the full viewer reuses the bytes.
import type { PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

let pdfjsLoad: Promise<typeof import('pdfjs-dist')> | null = null;
export const loadPdfjs = () => (pdfjsLoad ??= import('pdfjs-dist').then((m) => { m.GlobalWorkerOptions.workerSrc = workerUrl; return m; }));

interface Entry { doc: Promise<PDFDocumentProxy>; bytes: Promise<Uint8Array> }
const cache = new Map<string, Entry>();

/** True while the PDF behind this URL is still in memory (its signed URL may have expired meanwhile). */
export const isCached = (url: string) => cache.has(url);

/** Loads (once) the PDF behind a signed URL. The last 6 documents stay in memory. */
export function openPdf(url: string): Entry {
  const hit = cache.get(url);
  if (hit) { cache.delete(url); cache.set(url, hit); return hit; }
  const bytes = fetch(url).then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.arrayBuffer(); }).then((b) => new Uint8Array(b));
  // pdf.js takes ownership of (detaches) the buffer it gets: give it a copy, keep the original for downloads.
  const doc = Promise.all([loadPdfjs(), bytes]).then(([pdfjs, b]) => pdfjs.getDocument({ data: b.slice(), isEvalSupported: false }).promise);
  const entry = { doc, bytes };
  cache.set(url, entry);
  doc.catch(() => cache.delete(url));
  while (cache.size > 6) { const [k, v] = cache.entries().next().value!; cache.delete(k); v.doc.then((d) => d.destroy()).catch(() => {}); }
  return entry;
}
